<template>
  <v-flex
    column
    role="region"
    aria-label="Disenchantment Guide Page"
  >
    <v-parallax
      src="https://gm.goz.fun/wp-content/uploads/2023/03/disenchanting-cover.png"
      height="400"
      aria-label="Background artwork for the Disenchantment guide"
      role="img"
    />
    <v-container>
      <v-card
        role="region"
        aria-label="Introduction to Disenchantment"
      >
        <v-app-bar
          flat
          color="primary"
          class="font-weight-thin text-uppercase"
          role="heading"
          aria-level="1"
        >
          {{ $t('components.guides.pages.disenchantment.title') }}
        </v-app-bar>
        <v-card-text>
          {{ $t('components.guides.pages.disenchantment.description') }}
        </v-card-text>

        <div
          v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
          :key="errorsGroupIndex"
          aria-live="polite"
          role="alert"
        >
          <v-alert
            v-for="error in errorsGroup"
            :key="error.id"
            :type="error.type"
            :icon="error.icon"
            role="alert"
          >
            {{ error.msg }}
          </v-alert>
        </div>
      </v-card>

      <v-card
        flat
        role="search"
        aria-label="Search Disenchantable Items"
      >
        <v-layout
          py-8
          px-8
          pt-4
          my-7
          :class="{ column: $vuetify.breakpoint.mdAndDown }"
        >
          <v-text-field
            v-model="search"
            :label="$t('components.guides.pages.disenchantment.searchLabel')"
            aria-describedby="search-desc"
          />
          <span
            id="search-desc"
            class="sr-only"
          >
            {{ $t('components.guides.pages.disenchantment.searchDescription') }}
          </span>
          <v-btn
            aria-label="Search disenchantable items"
            class="mt-1 ml-2"
            color="accent"
            type="button"
            large
            @click="startSearching"
          >
            <v-icon
              small
              aria-hidden="true"
            >
              fad fa-search
            </v-icon>
          </v-btn>
        </v-layout>
      </v-card>

      <!-- Disenchantable Items Table -->
      <v-layout
        my-2
        mx-2
        column
        role="region"
        aria-label="Disenchantable Items List"
      >
        <v-card>
          <v-card-title
            role="heading"
            aria-level="2"
          >
            {{ $t('components.guides.pages.disenchantment.disenchantableItems.title') }}
          </v-card-title>
          <v-data-table
            :headers="disenchantableItemsData.headers"
            :items="disenchantableItemsData.items"
            :search="tableSearch"
            :loading="isLoading"
            loading-text="Loading... Please wait"
            no-results-text="Nothing was found"
            no-data-text="Nothing was found"
            :items-per-page="10"
            class="elevation-1"
            sort-by="item_name"
            group-desc
            color="secondary"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'fad fa-chevron-double-left',
              lastIcon: 'fad fa-chevron-double-right',
              prevIcon: 'fad fa-chevron-left',
              nextIcon: 'fad fa-chevron-right'
            }"
            :aria-label="$t('components.guides.pages.disenchantment.disenchantableItems.title')"
          >
            <template #progress>
              <v-progress-linear
                v-show="isLoading"
                color="accent"
                indeterminate
                aria-label="Loading disenchantable items"
                role="status"
              />
            </template>
          </v-data-table>
        </v-card>
      </v-layout>

      <v-layout
        my-2
        mx-2
        column
        role="region"
        aria-label="Obtainable Materials List"
      >
        <v-card>
          <v-card-title
            role="heading"
            aria-level="2"
          >
            {{ $t('components.guides.pages.disenchantment.obtainableMaterials.title') }}
          </v-card-title>
          <v-data-table
            :headers="obtainableMaterialsData.headers"
            :items="obtainableMaterialsData.items"
            :search="search"
            :loading="isLoading"
            loading-text="Loading... Please wait"
            no-results-text="Nothing was found"
            no-data-text="Nothing was found"
            :items-per-page="10"
            class="elevation-1"
            sort-by="item_name"
            group-desc
            color="secondary"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'fad fa-chevron-double-left',
              lastIcon: 'fad fa-chevron-double-right',
              prevIcon: 'fad fa-chevron-left',
              nextIcon: 'fad fa-chevron-right'
            }"
            :aria-label="$t('components.guides.pages.disenchantment.obtainableMaterials.title')"
          >
            <template #progress>
              <v-progress-linear
                v-show="isLoading"
                color="accent"
                indeterminate
                aria-label="Loading obtainable materials"
                role="status"
              />
            </template>
          </v-data-table>
        </v-card>
      </v-layout>
    </v-container>
  </v-flex>
</template>

<script>
import { fromGExcelToTable } from '@/utils/table.utils'
import { filterByKeys } from "@/utils/object.utils";
import Validator from "@/modules/validator.module";

const ValidatorModule = new Validator();

export default {
  name: "DisenchatmentCollection",
  inject: ["$validator"],
  data() {
    return {
        rawDisenchantableItemsData: '',
        rawObtainableMaterialsData: '',
        isLoading: false,
        search: '',
        tableSearch: '',
    }
  },
  computed: {
      disenchantableItemsData() {
          return fromGExcelToTable(this.rawDisenchantableItemsData) || {}
      },
      obtainableMaterialsData() {
          return fromGExcelToTable(this.rawObtainableMaterialsData) || {}
      },

      filteredErrors() {
        return filterByKeys(
            this.$validator.errors.collect(),
            []
        );
      },
  },
  beforeMount() {
    this.isLoading = true;

    this.fetchDisenchantableItems();
    this.fetchComponentObtainMaterial();
  },
  methods: {
      startSearching() {
          this.tableSearch = this.search;
      },
      fetchDisenchantableItems() {
          this.$api.getDisenchantableItems()
          .then((data) => {
             this.rawDisenchantableItemsData = data;

             this.isLoading = false;
          }).catch(() => {
              // Add to the validator
              ValidatorModule.add("disenchatment-error", {
                type: 0,
                msg: 'Something went wrong while fetching Disnechantment Components. Please refresh and try again'
              });

              this.isLoading = false;
              this.rawDisenchantableItemsData = {};
          });
      },
      fetchComponentObtainMaterial() {
          this.$api.getDisenchantmentObtainMaterials()
          .then((data) => {
             this.rawObtainableMaterialsData = data;

             this.isLoading = false;
          }).catch(() => {
              // Add to the validator
              ValidatorModule.add("disenchatment-error", {
                type: 0,
                msg: 'Something went wrong while fetching Disnechantment Materials. Please refresh and try again'
              });

              this.isLoading = false;
              this.rawObtainableMaterialsData = {};
          });
      },
  }
};
</script>
